import { AdminProjects, Cohorts, Expertises } from '@platform/client';
import { PillBadge, PillBadgeColors } from '@platform/components';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import cx from 'classix';
import { AdminProject, ProjectPublishingStage, ProjectStage } from '@platform/client/schemas';
import { useMemo, useContext, useState } from 'react';
import { getDaysSince } from '../../../util/DateHelpers';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { AdminListPageWrapper } from '../../AdminListPage';
import { Link } from 'wouter';
import { ADMIN_ROUTES, ROUTES } from '../../../routes/consts';
import { OrganizationLink } from '../organizations/OrganizationsAdmin';
import { ImportProjectsModal } from './ImportProjectsModal';
import { navigate } from 'wouter/use-browser-location';

const columnHelper = createColumnHelper<AdminProject>();

export const ProjectsAdmin = () => {
  return (
    <AdminListPageWrapper>
      <ProjectsAdminContent />
    </AdminListPageWrapper>
  );
};

const ProjectsAdminContent = () => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [importProjectsModalOpen, setImportProjectsModalOpen] = useState(false);
  const {
    data: projectsResponse,
    isError,
    isLoading,
    isSuccess,
  } = AdminProjects.useGetProjects(
    {
      ...adminQueryOptionsV2,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  // get cohort filter values
  const { data: cohortsData } = Cohorts.useGetCohorts();
  const { data: expertisesData } = Expertises.useGetExpertises();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          cell: (id) => id.getValue(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>ID</span>,
          size: 25,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('name', {
          cell: ({ row }) => {
            return <ProjectLink projectId={row.original.id} projectName={row.original.name} />;
          },
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Name</span>,
          size: 150,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('organizationName', {
          cell: ({ row }) => {
            return (
              <OrganizationLink
                organizationId={row.original.organizationId}
                organizationName={row.original.organizationName}
              />
            );
          },
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Organization</span>,
          size: 150,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('cohorts', {
          cell: (cohorts) => {
            return (cohorts.getValue() || []).map((cohort: string) => (
              <PillBadge variant='plain' color='blue' key={cohort} size='sm'>
                {cohort}
              </PillBadge>
            ));
          },
          header: () => (
            <span className='overflow-ellipsis whitespace-nowrap'>Planned Cohorts</span>
          ),
          size: 100,
          enableSorting: false,
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions: cohortsData?.cohorts.map((cohort) => ({
              label: cohort.name,
              value: cohort.name,
            })),
          },
        }),
        columnHelper.accessor('skills', {
          cell: (skills) => {
            return skills.getValue().map((skill: string) => (
              <PillBadge variant='plain' color='indigo' key={skill} size='sm'>
                {skill}
              </PillBadge>
            ));
          },
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Skills</span>,
          size: 100,
          enableSorting: false,
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions: expertisesData?.map((expertise) => ({
              label: expertise.name,
              value: expertise.name,
            })),
          },
        }),
        columnHelper.accessor('openToTeam', {
          cell: (openToTeam) => (openToTeam.getValue() ? 'Yes' : 'No'),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Teams</span>,
          size: 50,
          enableSorting: false,
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions: [{ label: 'Yes' }, { label: 'No' }],
          },
        }),
        columnHelper.accessor('publishingStage', {
          cell: ({ row, cell }) => {
            const stage = cell.getValue();
            const daysSinceUpdate = getDaysSince(row.original.publishingStageUpdatedDate);
            const daysSinceUpdateText =
              daysSinceUpdate === 0 ? 'today' : (
                `${daysSinceUpdate} day${daysSinceUpdate > 1 ? 's' : ''}`
              );
            const badgeColor = getProjectBadgeColor(stage, row.original.publishingStageUpdatedDate);
            return (
              <div className='flex justify-between align-middle'>
                <PillBadge variant='dot' size='md' color={badgeColor as PillBadgeColors}>
                  {stage}
                </PillBadge>
                {stage === 'review' && (
                  <p
                    className={cx(
                      stage === 'review' && daysSinceUpdate >= 6 && 'font-bold text-error-600',
                    )}
                  >
                    ({daysSinceUpdateText})
                  </p>
                )}
              </div>
            );
          },
          header: () => (
            <span className='overflow-ellipsis whitespace-nowrap'>Publishing Stage</span>
          ),
          size: 25,
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions: Object.values(ProjectPublishingStage).map((stage) => ({
              label: stage,
              value: stage,
            })),
          },
        }),
        columnHelper.accessor('stage', {
          cell: (stage) => stage.getValue(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Match Stage</span>,
          size: 25,
          meta: {
            filterVariant: 'multi-select',
            filterSelectOptions: Object.values(ProjectStage).map((stage) => ({
              label: stage,
              value: stage,
            })),
          },
        }),
        columnHelper.accessor('startDate', {
          cell: (startDate) => {
            const dateValue = startDate.getValue();
            return dateValue ? new Date(dateValue).toLocaleDateString() : 'N/A';
          },
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Start Date</span>,
          size: 25,
          meta: {
            filterVariant: 'date-select',
          },
        }),
        columnHelper.accessor('endDate', {
          cell: (endDate) => {
            const dateValue = endDate.getValue();
            return dateValue ? new Date(dateValue).toLocaleDateString() : 'N/A';
          },
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>End Date</span>,
          size: 25,
          meta: {
            filterVariant: 'date-select',
          },
        }),
        columnHelper.accessor('createdAt', {
          cell: (createdAt) => new Date(createdAt.getValue()).toLocaleDateString(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Created</span>,
          size: 25,
          meta: {
            filterVariant: 'date-select',
          },
        }),
      ] as ColumnDef<AdminProject>[],
    [cohortsData, expertisesData],
  );

  return (
    <>
      <AdminTable
        data={projectsResponse?.projects ?? []}
        columns={columns}
        filteredCount={projectsResponse?.filteredCount ?? 0}
        isLoading={isLoading}
        isError={isError}
        isSuccess={isSuccess}
        enableFiltering={true}
        tableActions={[
          {
            text: 'Import projects',
            callback: () => setImportProjectsModalOpen(true),
          },
          {
            text: 'Search',
            callback: () => {
              navigate(`platform${ROUTES.ADMIN}${ADMIN_ROUTES.SEARCH_PROJECTS}`);
            },
          },
        ]}
      />
      <ImportProjectsModal
        isOpen={importProjectsModalOpen}
        onClose={() => setImportProjectsModalOpen(false)}
      />
    </>
  );
};

export const getProjectBadgeColor = (
  stage: string,
  publishStageUpdatedString: string,
): PillBadgeColors => {
  const daysSinceUpdate = getDaysSince(publishStageUpdatedString);
  let badgeColor: PillBadgeColors = 'gray';
  if (stage === 'published') {
    badgeColor = 'success-500';
  } else if (stage === 'review' || stage === 'revision_requested') {
    badgeColor = stage === 'review' && daysSinceUpdate >= 6 ? 'error' : 'warning';
  }
  return badgeColor;
};

export const ProjectLink = ({
  projectId,
  projectName,
}: {
  projectId?: number | null;
  projectName?: string | null;
}) => {
  if (projectId && projectName) {
    return (
      <Link
        to={`~/platform${ROUTES.ADMIN}${ADMIN_ROUTES.PROJECTS}/${projectId}`}
        className='text-brand-500 hover:underline'
      >
        {projectName}
      </Link>
    );
  }
};
